import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  TableDetailFull,
  Activity,
  TeamMember,
  UserSummary,
  Note,
  FilesResponse,
  UploadedFile,
  SharePointLink,
  TableHistoryEntry,
  AccessRequest,
} from '@command-floor/shared-types';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

type TabId = 'home' | 'activities' | 'notes' | 'files' | 'chat' | 'contacts' | 'history';

const TABS: { id: TabId; label: string }[] = [
  { id: 'home', label: 'Home' },
  { id: 'activities', label: 'Activities' },
  { id: 'notes', label: 'Notes' },
  { id: 'files', label: 'Files' },
  { id: 'chat', label: 'Chat Log' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'history', label: 'History' },
];

export function TableHomePage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('home');

  const { data, isLoading, error } = useQuery({
    queryKey: ['table', id],
    queryFn: () => api.get<TableDetailFull>(`/api/tables/${id}`),
    enabled: !!id,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/api/users'),
  });

  if (isLoading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (error) {
    return (
      <div className="p-8 text-slate-300">
        <p>Could not load this work effort.</p>
        <p className="mt-1 text-sm text-slate-500">{(error as Error).message}</p>
      </div>
    );
  }
  if (!data) return null;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['table', id] });
  const canEdit =
    user?.role === 'LEADERSHIP' ||
    user?.role === 'ENTITY_MANAGER' ||
    data.team.some((m) => m.user_id === user?.id);
  const canFlag = canEdit;
  const canReview = user?.role === 'LEADERSHIP' || user?.role === 'ENTITY_MANAGER';
  const canArchive = canEdit;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Full-width table header ── */}
      <div className="flex-none border-b border-aurora bg-surface-panel px-6 py-4">
        {data.archived_at && (
          <div className="mb-3 flex items-center justify-between rounded border border-yellow-700/50 bg-yellow-900/20 px-4 py-2 text-sm text-yellow-300">
            <span>This work effort is archived.</span>
            {canArchive && <UnarchiveButton tableId={data.id} onDone={invalidate} />}
          </div>
        )}

        {/* Row 1: breadcrumb */}
        <p className="mb-1 text-xs uppercase tracking-wider text-slate-500">
          {data.entity.name} › {data.category.name}
          {data.subcategory ? ` › ${data.subcategory.name}` : ''}
        </p>

        {/* Row 2: table name + flagpole (left) + archive (right) */}
        <div className="flex items-center gap-4">
          <TableNameEditor
            tableId={data.id}
            name={data.name}
            canEdit={canEdit && !data.archived_at}
            onSaved={invalidate}
          />
          <FlagpoleIcon status={data.status_flag} blue={data.is_blue_for_viewer} />
          <div className="flex-1" />
          {canArchive && !data.archived_at && (
            <ArchiveButton tableId={data.id} onDone={invalidate} />
          )}
        </div>

        {/* Row 3: status + flag controls horizontal */}
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <FlagPill status={data.status_flag} blue={data.is_blue_for_viewer} />
          {canFlag && !data.archived_at && (
            <FlagControls
              tableId={data.id}
              currentFlag={data.status_flag}
              raisedById={data.status_flag_raised_by?.id ?? null}
              currentUserId={user?.id ?? ''}
              userRole={user?.role ?? 'TEAM_MEMBER'}
              onDone={invalidate}
            />
          )}
          {canReview && data.is_blue_for_viewer && (
            <MarkReviewedButton tableId={data.id} onDone={invalidate} />
          )}
        </div>

        {data.executive_lead && (
          <p className="mt-1.5 text-sm text-slate-400">
            Executive Lead:{' '}
            <span className="text-slate-200">{data.executive_lead.display_name}</span>
          </p>
        )}
        {data.status_flag !== 'GREY' && data.status_flag_reason && (
          <div className="mt-2 rounded border border-aurora-strong bg-slate-800 px-3 py-2 text-sm text-slate-300">
            <span className="font-medium text-slate-400">Flag reason: </span>
            {data.status_flag_reason}
          </div>
        )}
      </div>

      {/* ── Two-column body ── */}
      <div className="flex min-h-0 flex-1">
        {/* Left column — 1/3 */}
        <div className="w-80 flex-none overflow-y-auto border-r border-aurora px-4 py-4 xl:w-96">
          <HotBoxSection
            tableId={data.id}
            value={data.hot_box}
            canEdit={canEdit}
            onSaved={invalidate}
          />
          <SummarySection
            tableId={data.id}
            value={data.summary}
            canEdit={canEdit}
            onSaved={invalidate}
          />
          <TeamSection
            tableId={data.id}
            team={data.team}
            accessRequests={data.access_requests}
            allUsers={allUsers}
            canEdit={canEdit}
            onSaved={invalidate}
          />
        </div>

        {/* Right column — 2/3 */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* Tab nav */}
          <nav className="flex flex-none overflow-x-auto border-b border-aurora bg-surface-panel px-2">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap px-4 py-3 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-pyek-cyan font-medium text-slate-100'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === 'home' && (
              <HomeTab
                open={data.open_activities}
                completed={data.completed_activities}
                onComplete={(actId) =>
                  api.post(`/api/activities/${actId}/complete`, {}).then(invalidate)
                }
                canEdit={canEdit}
              />
            )}
            {activeTab === 'activities' && (
              <ActivitiesSection
                tableId={data.id}
                open={data.open_activities}
                completed={data.completed_activities}
                allUsers={allUsers}
                canEdit={canEdit}
                onSaved={invalidate}
              />
            )}
            {activeTab === 'notes' && (
              <NotesSection
                tableId={data.id}
                canEdit={canEdit}
                currentUserId={user?.id ?? ''}
                userRole={user?.role ?? 'TEAM_MEMBER'}
              />
            )}
            {activeTab === 'files' && <FilesSection tableId={data.id} canEdit={canEdit} />}
            {activeTab === 'history' && <HistoryTab tableId={data.id} />}
            {(activeTab === 'chat' || activeTab === 'contacts') && (
              <p className="text-sm italic text-slate-500">
                {activeTab === 'chat' && 'Chat log coming in a future phase.'}
                {activeTab === 'contacts' && 'Contacts coming in a future phase.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Home tab: priority activities overview ───────────────────────────────────

function HomeTab({
  open,
  completed,
  onComplete,
  canEdit,
}: {
  open: Activity[];
  completed: Activity[];
  onComplete: (id: string) => void;
  canEdit: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = open.filter((a) => a.due_date < today);
  const upcoming = open.filter((a) => a.due_date >= today);

  if (open.length === 0 && completed.length === 0) {
    return <p className="text-sm italic text-slate-500">No activities yet.</p>;
  }

  return (
    <div className="space-y-5">
      {overdue.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-flag-red">
            Overdue ({overdue.length})
          </p>
          <ActivityList items={overdue} today={today} canEdit={canEdit} onComplete={onComplete} />
        </div>
      )}
      {upcoming.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Open ({upcoming.length})
          </p>
          <ActivityList items={upcoming} today={today} canEdit={canEdit} onComplete={onComplete} />
        </div>
      )}
      {open.length === 0 && completed.length > 0 && (
        <p className="text-sm text-slate-400">
          All activities complete.{' '}
          <span className="text-slate-500">({completed.length} completed)</span>
        </p>
      )}
    </div>
  );
}

function ActivityList({
  items,
  today,
  canEdit,
  onComplete,
}: {
  items: Activity[];
  today: string;
  canEdit: boolean;
  onComplete: (id: string) => void;
}) {
  return (
    <ul className="space-y-2">
      {items.map((a) => {
        const isOverdue = a.due_date < today;
        const priorityColor =
          a.priority === 'HIGH'
            ? 'text-flag-red'
            : a.priority === 'MEDIUM'
              ? 'text-flag-yellow'
              : 'text-slate-500';
        return (
          <li
            key={a.id}
            className="flex items-start justify-between gap-3 rounded border border-aurora bg-slate-800/50 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-200">{a.name}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                <span className={priorityColor}>{a.priority}</span>
                {' · '}
                <span className={isOverdue ? 'text-flag-red' : ''}>Due {a.due_date}</span>
                {' · '}
                {a.assignee.display_name}
              </p>
            </div>
            {canEdit && (
              <button
                onClick={() => onComplete(a.id)}
                className="shrink-0 text-xs text-slate-400 hover:text-green-400"
              >
                ✓
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-lg border border-aurora bg-surface-panel p-4">
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        {title}
      </h2>
      {children}
    </section>
  );
}

function FlagpoleIcon({ status, blue }: { status: 'GREY' | 'YELLOW' | 'RED'; blue: boolean }) {
  const flagColor = blue
    ? '#2563EB'
    : status === 'RED'
      ? '#DC2626'
      : status === 'YELLOW'
        ? '#F59E0B'
        : null;
  return (
    <svg
      width="28"
      height="44"
      viewBox="0 0 28 44"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      {/* Pole */}
      <line x1="4" y1="4" x2="4" y2="42" stroke="#475569" strokeWidth="2" strokeLinecap="round" />
      {/* Finial */}
      <circle cx="4" cy="4" r="2.5" fill="#475569" />
      {/* Flag — absent when GREY (no flag raised) */}
      {flagColor && <path d="M5 6 L26 12 L5 18 Z" fill={flagColor} />}
    </svg>
  );
}

function FlagPill({ status, blue }: { status: 'GREY' | 'YELLOW' | 'RED'; blue: boolean }) {
  const color =
    status === 'RED'
      ? 'bg-flag-red text-white'
      : status === 'YELLOW'
        ? 'bg-flag-yellow text-slate-900'
        : 'bg-slate-600 text-slate-200';
  const label = status === 'RED' ? 'Stopped' : status === 'YELLOW' ? 'Help needed' : 'On track';
  return (
    <div className="flex flex-col items-end gap-1">
      <span className={`rounded-full px-3 py-1 text-xs font-medium ${color}`}>{label}</span>
      {blue && (
        <span className="rounded-full bg-flag-blue px-3 py-1 text-xs font-medium text-white">
          Next up for review
        </span>
      )}
    </div>
  );
}

function FlagControls({
  tableId,
  currentFlag,
  raisedById,
  currentUserId,
  userRole,
  onDone,
}: {
  tableId: string;
  currentFlag: 'GREY' | 'YELLOW' | 'RED';
  raisedById: string | null;
  currentUserId: string;
  userRole: string;
  onDone: () => void;
}) {
  const [showRaise, setShowRaise] = useState(false);
  const [color, setColor] = useState<'YELLOW' | 'RED'>('YELLOW');
  const [reason, setReason] = useState('');

  const canLower =
    userRole === 'LEADERSHIP' || userRole === 'ENTITY_MANAGER' || raisedById === currentUserId;

  const raise = useMutation({
    mutationFn: () => api.post(`/api/tables/${tableId}/flag`, { color, reason }),
    onSuccess: () => {
      setShowRaise(false);
      setReason('');
      onDone();
    },
  });

  const lower = useMutation({
    mutationFn: () => api.delete(`/api/tables/${tableId}/flag`),
    onSuccess: onDone,
  });

  if (currentFlag !== 'GREY') {
    return canLower ? (
      <button
        onClick={() => lower.mutate()}
        disabled={lower.isPending}
        className="text-xs text-slate-400 underline hover:text-slate-200"
      >
        {lower.isPending ? 'Clearing…' : 'Lower flag'}
      </button>
    ) : null;
  }

  return (
    <div>
      {!showRaise ? (
        <button
          onClick={() => setShowRaise(true)}
          className="text-xs text-slate-400 underline hover:text-slate-200"
        >
          Raise flag
        </button>
      ) : (
        <div className="w-64 rounded border border-aurora-strong bg-slate-800 p-3">
          <div className="mb-2 flex gap-2">
            {(['YELLOW', 'RED'] as const).map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`flex-1 rounded px-2 py-1 text-xs font-medium ${
                  color === c
                    ? c === 'YELLOW'
                      ? 'bg-flag-yellow text-slate-900'
                      : 'bg-flag-red text-white'
                    : 'bg-slate-700 text-slate-300'
                }`}
              >
                {c === 'YELLOW' ? 'Help needed' : 'Stopped'}
              </button>
            ))}
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required)"
            className="w-full resize-none rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
            rows={2}
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => raise.mutate()}
              disabled={!reason.trim() || raise.isPending}
              className="flex-1 rounded bg-slate-600 px-2 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {raise.isPending ? 'Raising…' : 'Raise'}
            </button>
            <button
              onClick={() => setShowRaise(false)}
              className="rounded px-2 py-1 text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MarkReviewedButton({ tableId, onDone }: { tableId: string; onDone: () => void }) {
  const mark = useMutation({
    mutationFn: () => api.post(`/api/tables/${tableId}/review`, {}),
    onSuccess: onDone,
  });
  return (
    <button
      onClick={() => mark.mutate()}
      disabled={mark.isPending}
      className="rounded-full bg-flag-blue px-3 py-1 text-xs font-medium text-white hover:opacity-80 disabled:opacity-50"
    >
      {mark.isPending ? 'Marking…' : 'Mark as Reviewed'}
    </button>
  );
}

function ArchiveButton({ tableId, onDone }: { tableId: string; onDone: () => void }) {
  const archive = useMutation({
    mutationFn: () => api.post(`/api/tables/${tableId}/archive`, {}),
    onSuccess: onDone,
  });
  return (
    <button
      onClick={() => archive.mutate()}
      disabled={archive.isPending}
      className="text-xs text-slate-500 underline hover:text-slate-300"
    >
      {archive.isPending ? 'Archiving…' : 'Archive'}
    </button>
  );
}

function UnarchiveButton({ tableId, onDone }: { tableId: string; onDone: () => void }) {
  const unarchive = useMutation({
    mutationFn: () => api.post(`/api/tables/${tableId}/unarchive`, {}),
    onSuccess: onDone,
  });
  return (
    <button
      onClick={() => unarchive.mutate()}
      disabled={unarchive.isPending}
      className="text-xs font-medium text-yellow-400 underline hover:text-yellow-200"
    >
      {unarchive.isPending ? 'Restoring…' : 'Unarchive'}
    </button>
  );
}

function TableNameEditor({
  tableId,
  name,
  canEdit,
  onSaved,
}: {
  tableId: string;
  name: string;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);

  const save = useMutation({
    mutationFn: () => api.patch(`/api/tables/${tableId}`, { name: value }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  if (!editing) {
    return (
      <h1
        className={`text-xl font-semibold tracking-tight text-slate-100 ${canEdit ? 'cursor-pointer hover:text-white' : ''}`}
        onClick={() => canEdit && setEditing(true)}
        title={canEdit ? 'Click to edit' : undefined}
      >
        {name}
      </h1>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="rounded border border-aurora-strong bg-slate-800 px-2 py-1 text-lg font-semibold text-slate-100"
      />
      <button
        onClick={() => save.mutate()}
        disabled={!value.trim() || save.isPending}
        className="rounded bg-slate-600 px-2 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
      >
        Save
      </button>
      <button
        onClick={() => {
          setValue(name);
          setEditing(false);
        }}
        className="text-xs text-slate-500 hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  );
}

function InlineEditor({
  tableId,
  field,
  value,
  canEdit,
  onSaved,
  placeholder,
  rows = 3,
}: {
  tableId: string;
  field: 'hot_box' | 'summary';
  value: string | null;
  canEdit: boolean;
  onSaved: () => void;
  placeholder: string;
  rows?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  const save = useMutation({
    mutationFn: () => api.patch(`/api/tables/${tableId}`, { [field]: draft || null }),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  if (!editing) {
    return (
      <div
        onClick={() => canEdit && setEditing(true)}
        className={
          canEdit ? 'cursor-pointer rounded p-1 -m-1 transition-colors hover:bg-slate-800' : ''
        }
      >
        {value ? (
          <p className="whitespace-pre-wrap text-sm text-slate-300">{value}</p>
        ) : (
          <p className="text-sm italic text-slate-600">{placeholder}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={rows}
        className="w-full resize-y rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
      />
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => {
            setDraft(value ?? '');
            setEditing(false);
          }}
          className="text-xs text-slate-500 hover:text-slate-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function HotBoxSection({
  tableId,
  value,
  canEdit,
  onSaved,
}: {
  tableId: string;
  value: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  return (
    <Section title="Hot Box">
      <InlineEditor
        tableId={tableId}
        field="hot_box"
        value={value}
        canEdit={canEdit}
        onSaved={onSaved}
        placeholder={canEdit ? "Click to add — what's most pressing right now." : 'Nothing posted.'}
        rows={3}
      />
    </Section>
  );
}

function SummarySection({
  tableId,
  value,
  canEdit,
  onSaved,
}: {
  tableId: string;
  value: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  return (
    <Section title="Summary">
      <InlineEditor
        tableId={tableId}
        field="summary"
        value={value}
        canEdit={canEdit}
        onSaved={onSaved}
        placeholder={canEdit ? 'Click to add a summary.' : 'No summary yet.'}
        rows={5}
      />
    </Section>
  );
}

function TeamSection({
  tableId,
  team,
  accessRequests,
  allUsers,
  canEdit,
  onSaved,
}: {
  tableId: string;
  team: TeamMember[];
  accessRequests: AccessRequest[];
  allUsers: UserSummary[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [roleLabel, setRoleLabel] = useState('');
  const teamUserIds = new Set(team.map((m) => m.user_id));
  const eligible = allUsers.filter((u) => !teamUserIds.has(u.id));
  const [userId, setUserId] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post(`/api/tables/${tableId}/team`, {
        user_id: userId,
        role_label: roleLabel || undefined,
      }),
    onSuccess: () => {
      setShowAdd(false);
      setUserId('');
      setRoleLabel('');
      onSaved();
    },
  });

  const remove = useMutation({
    mutationFn: (uid: string) => api.delete(`/api/tables/${tableId}/team/${uid}`),
    onSuccess: onSaved,
  });

  const approve = useMutation({
    mutationFn: (reqId: string) =>
      api.post(`/api/tables/${tableId}/access-requests/${reqId}/approve`, {}),
    onSuccess: onSaved,
  });

  const deny = useMutation({
    mutationFn: (reqId: string) => api.delete(`/api/tables/${tableId}/access-requests/${reqId}`),
    onSuccess: onSaved,
  });

  return (
    <Section title={`Team (${team.length})`}>
      {team.length === 0 ? (
        <p className="text-sm italic text-slate-600">No team members yet.</p>
      ) : (
        <ul className="mb-3 space-y-1">
          {team.map((m) => (
            <li key={m.user_id} className="flex items-center justify-between text-sm">
              <span className="text-slate-200">
                {m.display_name}
                {m.role_label && (
                  <span className="ml-2 text-xs text-slate-500">({m.role_label})</span>
                )}
              </span>
              {canEdit && (
                <button
                  onClick={() => remove.mutate(m.user_id)}
                  className="text-xs text-slate-600 hover:text-red-400"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Pending access requests — visible to anyone who can edit the table */}
      {canEdit && accessRequests.length > 0 && (
        <div className="mb-3 rounded border border-pyek-cyan/20 bg-pyek-navy/20 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-pyek-cyan">
            Access Requests ({accessRequests.length})
          </p>
          <ul className="space-y-2">
            {accessRequests.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-slate-200">{r.requester.display_name}</p>
                  {r.message && <p className="truncate text-xs text-slate-500">"{r.message}"</p>}
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <button
                    onClick={() => approve.mutate(r.id)}
                    disabled={approve.isPending}
                    className="rounded bg-pyek-cyan/20 px-2 py-1 text-xs font-medium text-pyek-cyan hover:bg-pyek-cyan/30 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => deny.mutate(r.id)}
                    disabled={deny.isPending}
                    className="rounded px-2 py-1 text-xs text-slate-500 hover:text-red-400 disabled:opacity-50"
                  >
                    Deny
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canEdit && !showAdd && (
        <button
          onClick={() => setShowAdd(true)}
          className="text-xs text-slate-400 underline hover:text-slate-200"
        >
          + Add member
        </button>
      )}

      {showAdd && (
        <div className="mt-2 space-y-2 rounded border border-aurora-strong bg-slate-800 p-3">
          {eligible.length === 0 ? (
            <p className="text-xs text-slate-500">All active users are already on this team.</p>
          ) : (
            <>
              <select
                autoFocus
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
              >
                <option value="">Select a person…</option>
                {eligible.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name}
                  </option>
                ))}
              </select>
              <input
                value={roleLabel}
                onChange={(e) => setRoleLabel(e.target.value)}
                placeholder="Role label (optional)"
                className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
              />
            </>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => add.mutate()}
              disabled={!userId || add.isPending}
              className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {add.isPending ? 'Adding…' : 'Add'}
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setUserId('');
                setRoleLabel('');
              }}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ─── Activities tab ───────────────────────────────────────────────────────────

function ActivitiesSection({
  tableId,
  open,
  completed,
  allUsers,
  canEdit,
  onSaved,
}: {
  tableId: string;
  open: Activity[];
  completed: Activity[];
  allUsers: UserSummary[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');

  const create = useMutation({
    mutationFn: () =>
      api.post(`/api/tables/${tableId}/activities`, {
        name,
        due_date: dueDate,
        assignee_id: assigneeId,
        priority,
      }),
    onSuccess: () => {
      setShowForm(false);
      setName('');
      setDueDate('');
      setPriority('MEDIUM');
      onSaved();
    },
  });

  const complete = useMutation({
    mutationFn: (actId: string) => api.post(`/api/activities/${actId}/complete`, {}),
    onSuccess: onSaved,
  });

  const uncomplete = useMutation({
    mutationFn: (actId: string) => api.post(`/api/activities/${actId}/uncomplete`, {}),
    onSuccess: onSaved,
  });

  const del = useMutation({
    mutationFn: (actId: string) => api.delete(`/api/activities/${actId}`),
    onSuccess: onSaved,
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      {open.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">Open</p>
          <ul className="space-y-2">
            {open.map((a) => (
              <ActivityRow
                key={a.id}
                activity={a}
                canEdit={canEdit}
                today={today}
                onComplete={() => complete.mutate(a.id)}
                onDelete={() => del.mutate(a.id)}
              />
            ))}
          </ul>
        </div>
      )}

      {completed.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
            Completed ({completed.length})
          </p>
          <ul className="space-y-2">
            {completed.slice(0, 5).map((a) => (
              <ActivityRow
                key={a.id}
                activity={a}
                canEdit={canEdit}
                today={today}
                onUncomplete={() => uncomplete.mutate(a.id)}
                onDelete={() => del.mutate(a.id)}
              />
            ))}
          </ul>
        </div>
      )}

      {open.length === 0 && completed.length === 0 && (
        <p className="mb-3 text-sm italic text-slate-600">No activities yet.</p>
      )}

      {canEdit && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="text-xs text-slate-400 underline hover:text-slate-200"
        >
          + Add activity
        </button>
      )}

      {showForm && (
        <div className="mt-2 space-y-2 rounded border border-aurora-strong bg-slate-800 p-3">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Activity name"
            className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
          />
          <div className="flex gap-2">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="flex-1 rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
              className="rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
            >
              <option value="LOW">Low</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
            </select>
          </div>
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
          >
            <option value="">Assign to…</option>
            {allUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.display_name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => create.mutate()}
              disabled={!name.trim() || !dueDate || !assigneeId || create.isPending}
              className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {create.isPending ? 'Adding…' : 'Add'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityRow({
  activity,
  canEdit,
  today,
  onComplete,
  onUncomplete,
  onDelete,
}: {
  activity: Activity;
  canEdit: boolean;
  today: string;
  onComplete?: () => void;
  onUncomplete?: () => void;
  onDelete?: () => void;
}) {
  const isOverdue = activity.status === 'OPEN' && activity.due_date < today;
  const priorityColor =
    activity.priority === 'HIGH'
      ? 'text-flag-red'
      : activity.priority === 'MEDIUM'
        ? 'text-flag-yellow'
        : 'text-slate-500';

  return (
    <li className="flex items-start justify-between gap-3 rounded border border-aurora bg-slate-800/50 px-3 py-2">
      <div className="min-w-0 flex-1">
        <p
          className={`text-sm ${activity.status === 'COMPLETE' ? 'text-slate-500 line-through' : 'text-slate-200'}`}
        >
          {activity.name}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">
          <span className={priorityColor}>{activity.priority}</span>
          {' · '}
          <span className={isOverdue ? 'text-flag-red' : ''}>Due {activity.due_date}</span>
          {' · '}
          {activity.assignee.display_name}
        </p>
      </div>
      {canEdit && (
        <div className="flex shrink-0 items-center gap-2">
          {activity.status === 'OPEN' && onComplete && (
            <button onClick={onComplete} className="text-xs text-slate-400 hover:text-green-400">
              ✓ Done
            </button>
          )}
          {activity.status === 'COMPLETE' && onUncomplete && (
            <button onClick={onUncomplete} className="text-xs text-slate-400 hover:text-slate-200">
              Reopen
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} className="text-xs text-slate-600 hover:text-red-400">
              ✕
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ─── Notes tab ────────────────────────────────────────────────────────────────

function NotesSection({
  tableId,
  canEdit,
  currentUserId,
  userRole,
}: {
  tableId: string;
  canEdit: boolean;
  currentUserId: string;
  userRole: string;
}) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [body, setBody] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');

  const { data: notes = [] } = useQuery({
    queryKey: ['notes', tableId],
    queryFn: () => api.get<Note[]>(`/api/tables/${tableId}/notes`),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notes', tableId] });

  const create = useMutation({
    mutationFn: () => api.post(`/api/tables/${tableId}/notes`, { body }),
    onSuccess: () => {
      setShowForm(false);
      setBody('');
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: (id: string) => api.patch(`/api/notes/${id}`, { body: editBody }),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/notes/${id}`),
    onSuccess: invalidate,
  });

  const canDelete = (note: Note) =>
    note.author.id === currentUserId || userRole === 'LEADERSHIP' || userRole === 'ENTITY_MANAGER';

  return (
    <div>
      {notes.map((note) => (
        <div key={note.id} className="mb-3 rounded border border-aurora bg-slate-800/50 px-3 py-2">
          {editingId === note.id ? (
            <div className="space-y-2">
              <textarea
                autoFocus
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={3}
                className="w-full resize-y rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => update.mutate(note.id)}
                  disabled={!editBody.trim() || update.isPending}
                  className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="whitespace-pre-wrap text-sm text-slate-200">{note.body}</p>
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-slate-500">
                  {note.author.display_name} · {new Date(note.created_at).toLocaleDateString()}
                  {note.last_edited_at && ' (edited)'}
                </p>
                <div className="flex gap-3">
                  {note.author.id === currentUserId && (
                    <button
                      onClick={() => {
                        setEditingId(note.id);
                        setEditBody(note.body);
                      }}
                      className="text-xs text-slate-500 hover:text-slate-300"
                    >
                      Edit
                    </button>
                  )}
                  {canDelete(note) && (
                    <button
                      onClick={() => del.mutate(note.id)}
                      className="text-xs text-slate-600 hover:text-red-400"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ))}

      {notes.length === 0 && !showForm && (
        <p className="mb-3 text-sm italic text-slate-600">No notes yet.</p>
      )}

      {canEdit && !showForm && (
        <button
          onClick={() => setShowForm(true)}
          className="text-xs text-slate-400 underline hover:text-slate-200"
        >
          + Add note
        </button>
      )}

      {showForm && (
        <div className="mt-2 space-y-2">
          <textarea
            autoFocus
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Write a note…"
            className="w-full resize-y rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
          />
          <div className="flex gap-2">
            <button
              onClick={() => create.mutate()}
              disabled={!body.trim() || create.isPending}
              className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {create.isPending ? 'Posting…' : 'Post'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setBody('');
              }}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Files tab ────────────────────────────────────────────────────────────────

function FilesSection({ tableId, canEdit }: { tableId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['files', tableId],
    queryFn: () => api.get<FilesResponse>(`/api/tables/${tableId}/files`),
  });
  const uploaded = data?.uploaded ?? [];
  const sharepoint = data?.sharepoint ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['files', tableId] });

  const addLink = useMutation({
    mutationFn: () =>
      api.post(`/api/tables/${tableId}/files/sharepoint`, {
        url: linkUrl,
        display_name: linkName,
      }),
    onSuccess: () => {
      setShowLinkForm(false);
      setLinkUrl('');
      setLinkName('');
      invalidate();
    },
  });

  const deleteFile = useMutation({
    mutationFn: (id: string) => api.delete(`/api/files/${id}`),
    onSuccess: invalidate,
  });

  const deleteLink = useMutation({
    mutationFn: (id: string) => api.delete(`/api/files/sharepoint/${id}`),
    onSuccess: invalidate,
  });

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`/api/tables/${tableId}/files/upload`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      invalidate();
    } catch (err) {
      setUploadError((err as Error).message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function formatBytes(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <div>
      {uploaded.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
            Uploaded
          </p>
          <ul className="space-y-1">
            {uploaded.map((f: UploadedFile) => (
              <li
                key={f.id}
                className="flex items-center justify-between rounded border border-aurora bg-slate-800/50 px-3 py-2"
              >
                <div className="min-w-0">
                  <a
                    href={`/api/files/${f.id}/download`}
                    className="block truncate text-sm text-flag-blue hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {f.filename}
                  </a>
                  <p className="text-xs text-slate-500">
                    {formatBytes(f.size_bytes)} · {f.uploaded_by.display_name} ·{' '}
                    {new Date(f.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => deleteFile.mutate(f.id)}
                    className="ml-3 shrink-0 text-xs text-slate-600 hover:text-red-400"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {sharepoint.length > 0 && (
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-slate-500">
            SharePoint / Links
          </p>
          <ul className="space-y-1">
            {sharepoint.map((l: SharePointLink) => (
              <li
                key={l.id}
                className="flex items-center justify-between rounded border border-aurora bg-slate-800/50 px-3 py-2"
              >
                <div className="min-w-0">
                  <a
                    href={l.url}
                    className="block truncate text-sm text-flag-blue hover:underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {l.display_name}
                  </a>
                  <p className="text-xs text-slate-500">
                    {l.added_by.display_name} · {new Date(l.added_at).toLocaleDateString()}
                  </p>
                </div>
                {canEdit && (
                  <button
                    onClick={() => deleteLink.mutate(l.id)}
                    className="ml-3 shrink-0 text-xs text-slate-600 hover:text-red-400"
                  >
                    ✕
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {uploaded.length === 0 && sharepoint.length === 0 && (
        <p className="mb-3 text-sm italic text-slate-600">No files yet.</p>
      )}

      {canEdit && (
        <div className="flex flex-wrap gap-3">
          <label className="cursor-pointer text-xs text-slate-400 underline hover:text-slate-200">
            {uploading ? 'Uploading…' : '+ Upload file'}
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>
          {!showLinkForm && (
            <button
              onClick={() => setShowLinkForm(true)}
              className="text-xs text-slate-400 underline hover:text-slate-200"
            >
              + Add SharePoint link
            </button>
          )}
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-flag-red">{uploadError}</p>}

      {showLinkForm && (
        <div className="mt-2 space-y-2 rounded border border-aurora-strong bg-slate-800 p-3">
          <input
            autoFocus
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            placeholder="Display name"
            className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
          />
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://pyek.sharepoint.com/…"
            className="w-full rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200 placeholder-slate-600"
          />
          <div className="flex gap-2">
            <button
              onClick={() => addLink.mutate()}
              disabled={!linkName.trim() || !linkUrl.trim() || addLink.isPending}
              className="rounded bg-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {addLink.isPending ? 'Adding…' : 'Add'}
            </button>
            <button
              onClick={() => {
                setShowLinkForm(false);
                setLinkUrl('');
                setLinkName('');
              }}
              className="text-xs text-slate-500 hover:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── History tab ──────────────────────────────────────────────────────────────

function describeEntry(e: TableHistoryEntry): string {
  const key = e.action;
  if (key === 'CREATE') {
    if (e.entity_type === 'TABLE') return 'created this work effort';
    if (e.entity_type === 'NOTE') return 'added a note';
    if (e.entity_type === 'FILE_UPLOADED') return 'uploaded a file';
    if (e.entity_type === 'FILE_SHAREPOINT') return 'added a SharePoint link';
    return 'created a record';
  }
  if (key === 'UPDATE') {
    if (e.entity_type === 'NOTE') return 'edited a note';
    if (e.entity_type === 'FILE_UPLOADED') return 'renamed a file';
    return 'updated a record';
  }
  if (key === 'DELETE') {
    if (e.entity_type === 'NOTE') return 'deleted a note';
    if (e.entity_type === 'FILE_UPLOADED') return 'deleted a file';
    if (e.entity_type === 'FILE_SHAREPOINT') return 'removed a SharePoint link';
    return 'deleted a record';
  }
  const after = e.after as Record<string, unknown> | null;
  const before = e.before as Record<string, unknown> | null;
  const ctx = e.context as Record<string, unknown> | null;
  switch (key) {
    case 'TABLE_EDIT': {
      const fields = after
        ? Object.keys(after)
            .map((k) => k.replace(/_/g, ' '))
            .join(', ')
        : '';
      return fields ? `updated ${fields}` : 'edited this work effort';
    }
    case 'TABLE_ARCHIVE':
      return 'archived this work effort';
    case 'TABLE_UNARCHIVE':
      return 'unarchived this work effort';
    case 'TABLE_RESTORE':
      return 'restored this work effort';
    case 'TABLE_DELETE':
      return 'deleted this work effort';
    case 'FLAG_RAISE': {
      const color = (after?.status_flag as string) === 'RED' ? 'red' : 'yellow';
      const reason = after?.reason as string | undefined;
      return reason ? `raised a ${color} flag — "${reason}"` : `raised a ${color} flag`;
    }
    case 'FLAG_LOWER':
      return 'cleared the flag';
    case 'MARK_REVIEWED': {
      const comment = ctx?.comment as string | undefined;
      return comment ? `marked as reviewed — "${comment}"` : 'marked as reviewed';
    }
    case 'TABLE_TEAM_ADD':
      return 'added a team member';
    case 'TABLE_TEAM_ROLE_CHANGE':
      return "updated a team member's role";
    case 'TABLE_TEAM_REMOVE':
      return 'removed a team member';
    case 'ACTIVITY_CREATE': {
      const name = (after?.name as string) ?? '';
      return name ? `added activity "${name}"` : 'added an activity';
    }
    case 'ACTIVITY_UPDATE': {
      const name = (before?.name as string) ?? '';
      return name ? `updated activity "${name}"` : 'updated an activity';
    }
    case 'ACTIVITY_COMPLETE': {
      const name = (before?.name as string) ?? '';
      return name ? `completed activity "${name}"` : 'completed an activity';
    }
    case 'ACTIVITY_UNCOMPLETE':
      return 're-opened an activity';
    case 'ACTIVITY_DELETE': {
      const name = (before?.name as string) ?? '';
      return name ? `deleted activity "${name}"` : 'deleted an activity';
    }
    default:
      return key.toLowerCase().replace(/_/g, ' ');
  }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function HistoryTab({ tableId }: { tableId: string }) {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['history', tableId],
    queryFn: () => api.get<TableHistoryEntry[]>(`/api/tables/${tableId}/history`),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Loading history…</p>;
  if (entries.length === 0)
    return <p className="text-sm italic text-slate-500">No history recorded yet.</p>;

  return (
    <div>
      {entries.map((e, i) => (
        <div key={e.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-slate-600" />
            {i < entries.length - 1 && <div className="w-px flex-1 bg-slate-700/60" />}
          </div>
          <div className="pb-4">
            <p className="text-sm text-slate-200">
              <span className="font-medium">{e.actor?.display_name ?? 'System'}</span>{' '}
              {describeEntry(e)}
            </p>
            <p
              className="mt-0.5 text-xs text-slate-500"
              title={new Date(e.occurred_at).toLocaleString()}
            >
              {relativeTime(e.occurred_at)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
